import { useEffect, useState } from 'react'
import type { DayDoc, DayResult } from '../lib/types'
import { loadDay, todayUtc } from '../lib/day'
import { getResult, saveResult } from '../lib/storage'
import { syncResult, isPlus } from '../lib/user'
import { emojiForResult, formatBig, timesOff } from '../lib/scoring'
import { Play } from './Play'
import { TopBar } from './TopBar'
import { BottomNav } from './BottomNav'

/** The Plus extension: 5 more on today's theme, scored separately from the shared daily. */
export function Extended({ onZoom }: { onZoom: (z: number) => void }) {
  const date = todayUtc()
  const key = `${date}-ext`
  const [day, setDay] = useState<DayDoc | null>(null)
  const [result, setResult] = useState<DayResult | undefined>(() => getResult(key))
  const [started, setStarted] = useState(false)
  const allowed = isPlus()
  useEffect(() => { if (!allowed) location.hash = '#/plus' }, [allowed])
  useEffect(() => { loadDay(date).then(setDay) }, [date])
  if (!allowed) return null
  if (!day) return <div className="screen landing"><p className="muted">Loading…</p></div>
  if (!day.extended?.length) return <div className="screen landing"><p className="muted">No extended pack today.</p><a className="btn" href="#/">Back</a></div>

  const asDay: DayDoc = { ...day, questions: day.extended }

  if (result) {
    return (
      <div className="screen results">
        <TopBar />
        <h1>✦ Extended pack done.</h1>
        <p className="sub">{day.theme.emoji} {day.theme.name} · 5 more, Plus only</p>
        <div className="emoji-grid">{[...result.emoji].map((e, k) => <span key={k}>{e}</span>)}</div>
        <div className="card"><h3>Score</h3><div className="score">{result.score.toLocaleString('en-US')}<small>/5,000</small></div><div className="tiny">extended packs don't affect your shared daily score</div></div>
        <div className="actions"><a className="btn block" href="#/">Back to today</a></div>
        <ul className="qlist">
          {asDay.questions.map((q, k) => { const r = result.results[k]; if (!r) return null; return (
            <li key={q.id}><span className="em">{emojiForResult(r)}</span><span><div>{q.prompt}</div><div className="p">you: {formatBig(r.guess)} · within {timesOff(r.ratio)} · +{r.qscore.toLocaleString('en-US')}</div></span><span className="a">{formatBig(q.answer)}<small>{q.unit}</small></span></li>
          ) })}
        </ul>
        <BottomNav />
      </div>
    )
  }

  if (!started) {
    return (
      <div className="screen landing">
        <TopBar />
        <div style={{ flex: 1 }} />
        <h1 style={{ fontSize: 40 }}>✦ 5 more on<br />{day.theme.name}</h1>
        <p>The extended pack — same theme, fresh numbers. Scored on its own, so the shared daily stays fair.</p>
        <button className="btn block" onClick={() => setStarted(true)}>Play the extension</button>
        <div style={{ flex: 1 }} />
      </div>
    )
  }

  return <Play day={asDay} replay practice onZoom={onZoom} onDone={(r) => {
    const dr = { ...r, date: key, replay: true }
    saveResult(dr)
    syncResult(dr, key)
    setResult(dr)
  }} />
}
